import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { aboutData } from '@/mocks/about';
import { categories } from '@/mocks/tvSeries';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setFormStatus('sending');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9ng958bdkpk99j7gj6g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于天美影视',
    description: aboutData.intro,
    url: 'https://www.tianmeitv.com/about',
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 天美影视 | 品牌介绍、口碑评价、联系方式"
        description={`${aboutData.intro}天美影视致力于为追剧爱好者提供最新、最全、最高清的电视剧资源。`}
        keywords="天美影视,关于天美影视,天美影视品牌,天美影视联系方式,免费影视平台"
        canonicalUrl="https://www.tianmeitv.com/about"
        jsonLd={[aboutSchema]}
      />

      <Navbar />

      <main className="pt-20 pb-14">
        <div className="w-full px-4 md:px-6">
          <div className="py-4">
            <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />
          </div>

          {/* Brand Intro Hero */}
          <section className="relative rounded-2xl overflow-hidden mb-14">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20office%20with%20warm%20ambient%20lighting%2C%20creative%20workspace%20with%20large%20monitors%20displaying%20film%20content%2C%20team%20collaboration%20atmosphere%2C%20professional%20media%20company%20interior%2C%20high%20quality%20production%20design&width=1600&height=500&seq=about-hero&orientation=landscape"
              alt="天美影视团队"
              className="w-full h-[300px] md:h-[400px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent flex items-end">
              <div className="p-6 md:p-10">
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">
                  关于天美影视
                </h1>
                <p className="text-white/80 text-sm md:text-base max-w-xl">
                  {aboutData.slogan}
                </p>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="mb-14">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {aboutData.stats.map((stat) => (
                <div key={stat.label} className="text-center p-5 rounded-xl bg-background-50 border border-background-200">
                  <div className="font-heading text-2xl md:text-3xl font-bold text-primary-500 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Mission + Vision */}
          <section className="mb-14">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-background-50 rounded-xl p-6 border border-background-200">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-3">
                  <i className="ri-focus-2-line text-lg"></i>
                </div>
                <h2 className="font-heading text-lg font-bold text-foreground-900 mb-2">我们的使命</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutData.mission}</p>
              </div>
              <div className="bg-background-50 rounded-xl p-6 border border-background-200">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-eye-line text-lg"></i>
                </div>
                <h2 className="font-heading text-lg font-bold text-foreground-900 mb-2">我们的愿景</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutData.vision}</p>
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="mb-14">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-8">
              为什么选择天美影视
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {aboutData.features.map((feat) => (
                <div key={feat.title} className="bg-background-50 rounded-xl p-5 border border-background-200 text-center hover:border-primary-200 transition-colors">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-3">
                    <i className={`${feat.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1.5">{feat.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{feat.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Timeline */}
          <section id="timeline" className="mb-14">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-8">
              发展历程
            </h2>
            <div className="relative max-w-2xl mx-auto">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 md:-translate-x-px" />
              <div className="space-y-8">
                {aboutData.timeline.map((item, i) => (
                  <div key={item.year} className={`relative flex items-start gap-4 md:gap-0 ${
                    i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}>
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-primary-500 -translate-x-1/2 mt-1.5 z-10 ring-4 ring-background-50" />
                    <div className={`ml-10 md:ml-0 md:w-1/2 ${i % 2 === 0 ? 'md:pr-10 md:text-right' : 'md:pl-10'}`}>
                      <span className="inline-block font-heading text-lg font-bold text-primary-500 mb-1">
                        {item.year}
                      </span>
                      <p className="text-sm text-foreground-600">{item.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section id="testimonials" className="mb-14 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6 py-12">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-8">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
              {aboutData.testimonials.map((t) => (
                <div key={t.name} className="bg-background-50 rounded-xl p-5 border border-background-200">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-fill text-background-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-3">&ldquo;{t.content}&rdquo;</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-xs font-bold">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section id="contact">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-8">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-0.5">电子邮箱</h3>
                    <p className="text-sm text-foreground-600">{aboutData.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-0.5">客服电话</h3>
                    <p className="text-sm text-foreground-600">{aboutData.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-0.5">公司地址</h3>
                    <p className="text-sm text-foreground-600">{aboutData.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-0.5">微信公众号</h3>
                    <p className="text-sm text-foreground-600">{aboutData.contact.wechat}</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 rounded-xl p-5 border border-background-200">
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">发送消息</h3>
                <form onSubmit={handleSubmit} data-readdy-form="" id="about-contact-form">
                  <div className="space-y-3">
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1">姓名</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-3 py-2 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1">邮箱</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-3 py-2 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1">留言</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={3}
                        className="w-full px-3 py-2 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      />
                    </div>
                    <div className="honeypot-field">
                      <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'sending'}
                      className="w-full py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-all disabled:opacity-50 whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'sending' ? '发送中...' : '发送消息'}
                    </button>
                  </div>
                  {formStatus === 'success' && (
                    <p className="text-xs text-green-600 mt-2 text-center">消息已发送成功，我们会尽快回复您！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-xs text-red-500 mt-2 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}